import { MANIA_LANE_COUNT, type ManiaLane } from '@shared/maniaNote';
import {
  HIGHWAY_TOP_Y_FRAC,
  LANE_GAP_PX,
  LANE_STACK_HORIZONTAL_PAD_PX,
  PERSPECTIVE_TOP_SCALE,
  PLAYING_FIELD_MAX_WIDTH_PX,
  STRUM_BAR_Y_FRAC,
} from './render/guitarRenderConstants';

/**
 * Shared playfield geometry for Guitar mode. Used by the renderer
 * (to draw the trapezoid highway, lane stripes, frets, and gems) and
 * the input source (to project pointer events to a lane). Keeping
 * the math in one module stops the two layers from diverging — a
 * divergence would let taps land on a different lane from the
 * visible gem the player sees.
 *
 * Mirrors `pianoPlayfieldGeometry.ts` but the highway is a perspective
 * trapezoid: at the strum bar (bottom) the lanes are full-width,
 * and at the highway top they converge to
 * {@link PERSPECTIVE_TOP_SCALE} × the bottom width.
 */

/**
 * Cached layout geometry derived from the current canvas CSS size.
 * The renderer recomputes this once per frame at the top of draw()
 * and threads it through every helper that needs lane / gem /
 * strum-bar positions so the math lives in one place. The input
 * source recomputes it per pointer event (events are sparse compared
 * to frames, so the extra layout work is negligible).
 */
export interface GuitarLayout {
  /** Y coordinate of the strum bar (where gems are tapped). */
  strumBarY: number;
  /** Y coordinate of the highway's far horizon (where gems spawn). */
  highwayTopY: number;
  /** Centre-x of the playing field — the perspective vanishing line. */
  centreX: number;
  /** Bottom (foreground) lane stack width. */
  bottomStackWidth: number;
  /** Top (far horizon) lane stack width. */
  topStackWidth: number;
  /** Bottom-foreground width of one lane in CSS px. */
  bottomLaneWidth: number;
  /** Top-far width of one lane in CSS px. */
  topLaneWidth: number;
  /** Gem height at the strum bar (CSS px). */
  gemHeight: number;
}

/**
 * Compute the perspective layout for the current canvas size. The
 * highway runs from `highwayTopY` (at `topStackWidth` total lane
 * width) down to `strumBarY` (at `bottomStackWidth`), with lane
 * boundaries linearly interpolated by progress fraction. Lane gaps
 * are absorbed into the bottom width and projected through the
 * trapezoid so the visual gutter narrows along with each lane.
 */
export function computeLayout(canvasWidth: number, canvasHeight: number): GuitarLayout {
  const playingFieldWidth = Math.min(canvasWidth, PLAYING_FIELD_MAX_WIDTH_PX);
  const centreX = canvasWidth / 2;
  const bottomStackWidth = playingFieldWidth - LANE_STACK_HORIZONTAL_PAD_PX * 2;
  const topStackWidth = bottomStackWidth * PERSPECTIVE_TOP_SCALE;
  const bottomLaneWidth =
    (bottomStackWidth - LANE_GAP_PX * (MANIA_LANE_COUNT - 1)) / MANIA_LANE_COUNT;
  const topLaneWidth =
    (topStackWidth - LANE_GAP_PX * PERSPECTIVE_TOP_SCALE * (MANIA_LANE_COUNT - 1)) /
    MANIA_LANE_COUNT;
  const strumBarY = canvasHeight * STRUM_BAR_Y_FRAC;
  const highwayTopY = canvasHeight * HIGHWAY_TOP_Y_FRAC;
  const gemHeight = canvasHeight * 0.062;
  return {
    strumBarY,
    highwayTopY,
    centreX,
    bottomStackWidth,
    topStackWidth,
    bottomLaneWidth,
    topLaneWidth,
    gemHeight,
  };
}

/**
 * Return the centre-x of `lane` at vertical position `progress`,
 * where 0 = highway top (far) and 1 = strum bar (foreground). Used
 * by the renderer to plot gems sliding down the trapezoid and by
 * the FX system to anchor particles to the lane the player just
 * tapped.
 */
export function laneCentreXAt(
  layout: GuitarLayout,
  lane: ManiaLane,
  progress: number,
): number {
  const t = Math.max(0, Math.min(1, progress));
  const stackWidth = layout.topStackWidth + (layout.bottomStackWidth - layout.topStackWidth) * t;
  const laneWidth =
    (stackWidth - LANE_GAP_PX * (1 - (1 - PERSPECTIVE_TOP_SCALE) * (1 - t)) * (MANIA_LANE_COUNT - 1)) /
    MANIA_LANE_COUNT;
  const stackLeft = layout.centreX - stackWidth / 2;
  const gap = (stackWidth - laneWidth * MANIA_LANE_COUNT) / (MANIA_LANE_COUNT - 1);
  return stackLeft + lane * (laneWidth + gap) + laneWidth / 2;
}

/**
 * Width of a single lane gem at vertical progress `progress` (0 = far
 * horizon, 1 = strum bar). Linearly interpolated between
 * `topLaneWidth` and `bottomLaneWidth`.
 */
export function laneWidthAt(layout: GuitarLayout, progress: number): number {
  const t = Math.max(0, Math.min(1, progress));
  return layout.topLaneWidth + (layout.bottomLaneWidth - layout.topLaneWidth) * t;
}

/**
 * Y coordinate at vertical progress `progress` (0 = far horizon,
 * 1 = strum bar). Linearly interpolated between `highwayTopY` and
 * `strumBarY`.
 */
export function highwayYAt(layout: GuitarLayout, progress: number): number {
  const t = Math.max(0, Math.min(1, progress));
  return layout.highwayTopY + (layout.strumBarY - layout.highwayTopY) * t;
}

/**
 * Convert a linear (time-based) approach progress into the
 * perspective-correct on-screen progress (0 = far horizon, 1 = strum
 * bar). A gem advancing at constant 3D depth velocity covers very
 * little screen-y near the horizon and accelerates as it nears the
 * camera — this is the iconic Guitar Hero "gems pour out of the back
 * wall and rush to the strum bar" feel, derived from real 3D
 * projection rather than a hand-tuned easing curve.
 *
 * Math (camera at z=0 looking down the highway; screen-y ∝ 1/z):
 *
 *   z_far  = 1 / PERSPECTIVE_TOP_SCALE  (depth at the horizon)
 *   z_near = 1                          (depth at the strum bar)
 *   z(t)   = z_far - (z_far - z_near) * t        (linear in time)
 *   screenProgress(t) = (1/z(t) - 1/z_far) / (1/z_near - 1/z_far)
 *                     = topScale·t / (1 - (1-topScale)·t)
 *
 * Clamped to [0, 1] so callers don't have to guard out-of-range
 * inputs (the formula breaks down past t = 1/(1-topScale) ≈ 1.47).
 */
export function perspectiveProgress(t: number): number {
  if (t <= 0) {
    return 0;
  }
  if (t >= 1) {
    return 1;
  }
  const oneMinusTopScale = 1 - PERSPECTIVE_TOP_SCALE;
  return (PERSPECTIVE_TOP_SCALE * t) / (1 - oneMinusTopScale * t);
}

/**
 * Project a pointer event's client-x to a lane index, or null if the
 * tap landed outside the visible foreground lane stack. Like the
 * Piano version, the foreground lane stack is the only area that
 * visually reads as a tap target — taps in the background trapezoid
 * (above the strum bar's near horizon) don't make sense as input
 * because the gems only "land" at the strum bar.
 *
 * Y is deliberately not gated: every Y inside the canvas is forwarded
 * to a lane based on its X — so an early tap that lands above the
 * strum bar still resolves to its lane, letting the engine apply its
 * own early-window heuristics. This mirrors the Piano behaviour.
 */
export function laneFromClientX(
  canvas: HTMLCanvasElement,
  clientX: number,
): ManiaLane | null {
  const rect = canvas.getBoundingClientRect();
  if (rect.width <= 0) {
    return null;
  }
  const layout = computeLayout(rect.width, rect.height);
  const localX = clientX - rect.left;
  const stackLeft = layout.centreX - layout.bottomStackWidth / 2;
  const stackRight = stackLeft + layout.bottomStackWidth;
  if (localX < stackLeft || localX > stackRight) {
    return null;
  }
  const slot = layout.bottomLaneWidth + LANE_GAP_PX;
  const rawIdx = Math.floor((localX - stackLeft) / slot);
  const idx = Math.max(0, Math.min(MANIA_LANE_COUNT - 1, rawIdx));
  return idx as ManiaLane;
}
